import type { Settings as ProLayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, setLocale, useAntdConfigSetter } from '@umijs/max';
import { AiChat } from '@zhiyue/ai-chat';
import {
  type LayoutSettings as CoreLayoutSettings,
  type CoreSessionConfig,
  type CurrentUser,
  isAdminOfCurrentOrganization,
  SessionProvider,
  ThemeToggle,
  useSession,
  useShell,
  useTheme,
} from '@zhiyue/core';
import '@zhiyue/core/style.css';
import '@zhiyue/lead/style.css';
import '@zhiyue/schedule/style.css';
import '@zhiyue/task-management/style.css';

import { Button, Result, Spin } from 'antd';
import { BizAppConfig } from 'config/application.manifest';
import {
  getBrandAppName,
  getStoredBrandLocale,
  syncBrandDocument,
} from 'config/brand';
import React from 'react';
import {
  getBootstrapTenantList,
  sessionAdapter,
} from '@/adapters/sessionAdapter';
import SiderResizeHandle, {
  DEFAULT_SIDER_WIDTH,
  getStoredSiderWidth,
} from '@/components/Layout/SiderResizeHandle';
import NotificationIcon from '@/components/NotificationIcon';
import {
  type AuthorizedResourceAccessMap,
  buildAuthorizedResourceAccessMap,
} from '@/permissions/adapter';
import {
  clearAutoRefresh,
  ensureValidAccessToken,
  getAccessToken,
  hasOAuth2Session,
  redirectToSSO,
  setupAutoRefresh,
} from '@/services/oauth2';
import {
  clearOpenPlatformToken,
  fetchOpenPlatformToken,
  getCachedOpenPlatformToken,
} from '@/services/openPlatformToken';
import {
  getLayoutVisibility,
  updateSessionSnapshot,
} from '@/state/sessionSnapshot';
import defaultSettings from '../config/defaultSettings';
import { readCurrentTenantUuid } from './adapters/tenantStorage';
import { errorConfig } from './requestErrorConfig';
import { getMyAuthorizedResources } from './services/bff/permission.api';
import '@ant-design/v5-patch-for-react-19';
import HeaderUserMenu from '@/components/Layout/HeaderUserMenu';
import {
  getAppTheme,
  getInitialNavTheme,
  getNavigationToken,
} from '@/theme/config';
import { ThemeBoundary } from '@/theme/ThemeBoundary';

export const request: RequestConfig = {
  ...errorConfig,
};

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';
const publicPaths = [loginPath, '/callback'];

const getSettingsWithSiderWidth = (locale?: string) => ({
  ...defaultSettings,
  title: getBrandAppName(locale),
  siderWidth: getStoredSiderWidth(),
  navTheme: getInitialNavTheme(),
  token: getNavigationToken(getInitialNavTheme()),
});

const getUserSyncKey = (
  user?:
    | (Partial<CurrentUser> & {
        uuid?: string;
      })
    | null,
) => {
  if (!user) {
    return '';
  }

  return [user.id, user.uuid, user.username, user.tenantUuid]
    .filter(Boolean)
    .join('|');
};

const getSessionConfig = (): CoreSessionConfig => {
  getInitialNavTheme();
  return {
    redirectAfterLogout: '/user/login',
    adapter: sessionAdapter,
    ui: {
      links: {
        help: 'https://docs.shanhuyun.cn/',
      },
      userMenu: {
        items: [],
        personalCenterUrl: BizAppConfig.appPersonalCenterUrl,
        workbenchUrl: BizAppConfig.appWorkbenchUrl,
        tenantManagementUrl: BizAppConfig.appTenantManagementUrl,
        openPlatformUrl: BizAppConfig.appOpenPlatformUrl,
      },
      locales: [
        { key: 'en-US', label: 'English', icon: '🇺🇸', title: 'Language' },
        { key: 'ja-JP', label: '日本語', icon: '🇯🇵', title: '言語' },
        { key: 'zh-CN', label: '简体中文', icon: '🇨🇳', title: '语言' },
        { key: 'zh-TW', label: '繁體中文', icon: '🇭🇰', title: '語言' },
      ],
    },
    featureFlags: {
      showHeader: true,
      showNav: true,
      showFooter: true,
      enableTenantSwitch: true,
      enableRegister: false,
    },
    onTenantSwitched: () => {
      window.location.href = '/';
    },
    onLocaleChanged: (locale) => {
      setLocale(locale, false);
    },
    onUnauthorized: () => {
      if (!publicPaths.includes(history.location.pathname)) {
        redirectToSSO();
      }
    },
  };
};

const HeaderTitle: React.FC<{
  logo: React.ReactNode;
  appTitle: string;
}> = ({ logo, appTitle }) => {
  return (
    <div className="app-header-title-root">
      <button
        type="button"
        className="app-header-title-main"
        onClick={() => history.push('/')}
      >
        {logo}
        <span className="app-header-title-name">{appTitle}</span>
      </button>
    </div>
  );
};

const SessionSync: React.FC<{ setInitialState: (updater: any) => void }> = ({
  setInitialState,
}) => {
  const { navTheme } = useTheme();
  const { currentUser, loading } = useSession();
  const { shellConfig } = useShell();
  const setAntdConfig = useAntdConfigSetter();
  const [runtimeLocale, setRuntimeLocale] = React.useState(
    getStoredBrandLocale(),
  );
  const themeSyncedRef = React.useRef<CoreLayoutSettings['navTheme'] | null>(
    null,
  );
  const syncedRef = React.useRef({
    navTheme: '',
    userSyncKey: '',
  });
  const userSyncKey = React.useMemo(
    () => getUserSyncKey(currentUser),
    [currentUser],
  );

  React.useEffect(() => {
    updateSessionSnapshot({
      currentUser,
      loading,
      featureFlags: shellConfig.featureFlags,
    });

    if (typeof setInitialState !== 'function') {
      return;
    }

    const lastSynced = syncedRef.current;
    if (
      lastSynced.navTheme === navTheme &&
      lastSynced.userSyncKey === userSyncKey
    ) {
      return;
    }

    setInitialState((prev: any) => {
      if (!prev) return prev;
      const prevUserSyncKey = getUserSyncKey(prev.currentUser);
      if (
        prev.settings?.navTheme === navTheme &&
        prevUserSyncKey === userSyncKey
      ) {
        return prev;
      }

      return {
        ...prev,
        currentUser,
        settings: {
          ...prev.settings,
          navTheme,
          token: getNavigationToken(
            navTheme === 'realDark' ? 'realDark' : 'light',
          ),
        },
      };
    });

    syncedRef.current = {
      navTheme,
      userSyncKey,
    };
  }, [
    currentUser,
    loading,
    navTheme,
    setInitialState,
    shellConfig.featureFlags,
    userSyncKey,
  ]);

  React.useEffect(() => {
    if (loading) {
      return;
    }

    if (currentUser && hasOAuth2Session()) {
      setupAutoRefresh();
      return;
    }

    clearAutoRefresh();
  }, [currentUser, loading]);

  React.useEffect(() => {
    const useDarkMode = navTheme === 'realDark';

    if (typeof document !== 'undefined') {
      const root = document.documentElement;
      const appTheme = useDarkMode ? 'dark' : 'light';
      root.dataset.appTheme = appTheme;
      root.style.colorScheme = appTheme;
    }

    if (themeSyncedRef.current === navTheme) {
      return;
    }
    themeSyncedRef.current = navTheme;

    setAntdConfig((prevConfig) => {
      return {
        ...prevConfig,
        theme: getAppTheme(navTheme === 'realDark' ? 'realDark' : 'light'),
      };
    });
  }, [navTheme, setAntdConfig]);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const syncLocale = () => {
      setRuntimeLocale(getStoredBrandLocale());
    };

    syncLocale();
    window.addEventListener('languagechange', syncLocale);
    window.addEventListener('storage', syncLocale);

    return () => {
      window.removeEventListener('languagechange', syncLocale);
      window.removeEventListener('storage', syncLocale);
    };
  }, []);

  React.useEffect(() => {
    const nextTitle = syncBrandDocument(runtimeLocale);

    if (typeof setInitialState !== 'function') {
      return;
    }

    setInitialState((prev: any) => {
      if (!prev) return prev;

      const prevSettings = prev.settings || {};
      if (prevSettings.title === nextTitle) {
        return prev;
      }

      return {
        ...prev,
        settings: {
          ...prevSettings,
          title: nextTitle,
        },
      };
    });
  }, [runtimeLocale, setInitialState]);

  return null;
};

const OpenPlatformTokenGate: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { currentUser, loading } = useSession();
  const [token, setToken] = React.useState(() => getCachedOpenPlatformToken());
  const [error, setError] = React.useState<Error | null>(null);
  const hasRequestedInitialRefreshRef = React.useRef(false);
  const refreshingRef = React.useRef(false);

  const loadToken = React.useCallback(async (forceRefresh = false) => {
    if (refreshingRef.current) {
      return;
    }

    refreshingRef.current = true;
    setError(null);
    try {
      if (forceRefresh) {
        clearOpenPlatformToken();
      }

      await fetchOpenPlatformToken();
      setToken(getCachedOpenPlatformToken());
    } catch (reason) {
      setToken(null);
      setError(
        reason instanceof Error ? reason : new Error('开放平台令牌获取失败'),
      );
    } finally {
      refreshingRef.current = false;
    }
  }, []);

  React.useEffect(() => {
    if (!loading && currentUser && !hasRequestedInitialRefreshRef.current) {
      hasRequestedInitialRefreshRef.current = true;
      void loadToken(true);
    }
  }, [currentUser, loadToken, loading]);

  React.useEffect(() => {
    if (!loading && currentUser && !token && !refreshingRef.current) {
      void loadToken();
    }
  }, [currentUser, loadToken, loading, token]);

  React.useEffect(() => {
    if (!token) {
      return;
    }

    const refreshDelay = Math.max(0, token.expiresAt - Date.now() - 60 * 1000);
    const timer = window.setTimeout(() => {
      setToken(null);
    }, refreshDelay);

    return () => window.clearTimeout(timer);
  }, [token]);

  if (loading || !currentUser || token) {
    return <>{children}</>;
  }

  if (error) {
    return (
      <Result
        status="error"
        title="开放平台服务暂不可用"
        subTitle="请重试后继续使用"
        extra={
          <Button type="primary" onClick={() => void loadToken()}>
            重试
          </Button>
        }
      />
    );
  }

  return <Spin fullscreen />;
};

const HeaderSessionContainer: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return <div className="app-header-actions">{children}</div>;
};

export const rootContainer = (
  container: React.ReactNode,
  {
    setInitialState,
  }: { initialState: any; setInitialState: (updater: any) => void },
) => {
  return (
    <RootSessionContainer setInitialState={setInitialState}>
      {container}
    </RootSessionContainer>
  );
};

const RootSessionContainer: React.FC<{
  setInitialState: (updater: any) => void;
  children: React.ReactNode;
}> = ({ setInitialState, children }) => {
  const sessionConfig = React.useMemo(() => getSessionConfig(), []);

  return (
    <SessionProvider config={sessionConfig}>
      <ThemeBoundary>
        <SessionSync setInitialState={setInitialState} />
        <OpenPlatformTokenGate>{children}</OpenPlatformTokenGate>
        <FloatingAiChat />
      </ThemeBoundary>
    </SessionProvider>
  );
};

const FloatingAiChat: React.FC = () => {
  const { currentUser, loading } = useSession();
  const { navTheme } = useTheme();

  if (
    loading ||
    !currentUser ||
    publicPaths.includes(history.location.pathname)
  ) {
    return null;
  }

  const profile = currentUser as CurrentUser & {
    organizationUuid?: string;
    lastLoginOrganizationUuid?: string;
  };

  return (
    <AiChat
      title="AI 助手"
      position="bottom-right"
      theme={navTheme === 'realDark' ? 'dark' : 'light'}
      authHeaders={() => {
        const headers: Record<string, string> = {};
        const token = getAccessToken();
        const tenantUuid = readCurrentTenantUuid() || currentUser.tenantUuid;
        const organizationUuid =
          profile.organizationUuid || profile.lastLoginOrganizationUuid;

        if (token) headers.Authorization = `Bearer ${token}`;
        if (tenantUuid) headers['x-tenant-id'] = tenantUuid;
        if (organizationUuid) headers['x-organization-id'] = organizationUuid;
        return headers;
      }}
    />
  );
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<ProLayoutSettings & CoreLayoutSettings>;
  currentUser?: API.CurrentUser & CurrentUser;
  authorizedResourceAccess?: AuthorizedResourceAccessMap;
  currentTenantIsAdmin?: boolean;
  loading?: boolean;
}> {
  let authorizedResourceAccess: AuthorizedResourceAccessMap = {};
  let currentTenantIsAdmin = false;

  // callback 页面里会在 token 落库后主动 refresh 初始态；
  // 这里不能再排除 /callback，否则首登回跳后权限态会一直是空的。
  if (hasOAuth2Session()) {
    const [authorizedResourcesResult, tenantListResult] =
      await Promise.allSettled([
        getMyAuthorizedResources(BizAppConfig.appSsoClientId || ''),
        getBootstrapTenantList(),
      ]);
    if (authorizedResourcesResult.status === 'fulfilled') {
      authorizedResourceAccess = buildAuthorizedResourceAccessMap(
        authorizedResourcesResult.value,
      );
    }

    if (tenantListResult.status === 'fulfilled') {
      const tenantList = tenantListResult.value ?? [];
      const currentTenantUuid = readCurrentTenantUuid() || tenantList[0]?.id;
      // 按当前企业判断：子企业管理员在本企业是管理员，在主组织只是成员
      currentTenantIsAdmin = isAdminOfCurrentOrganization(
        tenantList.find((tenant) => tenant.id === currentTenantUuid),
      );
    }
  }
  return {
    loading: true,
    authorizedResourceAccess,
    currentTenantIsAdmin,
    settings: getSettingsWithSiderWidth(getStoredBrandLocale()) as Partial<
      ProLayoutSettings & CoreLayoutSettings
    >,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  // rootContainer does not receive the model setter. Read the live session
  // theme here instead of relying on the initial settings snapshot.
  const { navTheme } = useTheme();
  const navigationTheme = navTheme === 'realDark' ? 'realDark' : 'light';
  const { collapsed: _ignoredCollapsed, ...layoutSettings } =
    (initialState?.settings || {}) as Partial<
      ProLayoutSettings & CoreLayoutSettings
    > & {
      collapsed?: boolean;
    };
  const getCurrentLayoutVisibility = () =>
    getLayoutVisibility(history.location.pathname);
  const appTitle = initialState?.settings?.title || getBrandAppName();
  const siderWidth =
    (
      initialState?.settings as Partial<CoreLayoutSettings> & {
        siderWidth?: number;
      }
    )?.siderWidth ?? DEFAULT_SIDER_WIDTH;
  const [collapsed, setCollapsed] = React.useState(true);
  const mountedAtRef = React.useRef(Date.now());

  const resizeEnabled =
    typeof window !== 'undefined' &&
    window.innerWidth > 768 &&
    initialState?.settings?.layout !== 'top' &&
    !collapsed;

  const handleResizeWidth = React.useCallback(
    (nextWidth: number) => {
      setInitialState((prev) => {
        if (!prev) return prev;
        const prevSettings =
          (prev.settings as Partial<CoreLayoutSettings> & {
            siderWidth?: number;
          }) || {};
        if (prevSettings.siderWidth === nextWidth) {
          return prev;
        }
        return {
          ...prev,
          settings: {
            ...prevSettings,
            siderWidth: nextWidth,
          },
        };
      });
    },
    [setInitialState],
  );

  const handleSiderCollapse = React.useCallback(
    (collapsed: boolean) => {
      const elapsed = Date.now() - mountedAtRef.current;
      if (collapsed === false && elapsed < 800) {
        return;
      }
      setCollapsed(collapsed);
      setInitialState((prev) => {
        if (!prev) return prev;
        const prevSettings =
          (prev.settings as Partial<CoreLayoutSettings> & {
            collapsed?: boolean;
          }) || {};
        if (prevSettings.collapsed === collapsed) {
          return prev;
        }
        return {
          ...prev,
          settings: {
            ...prevSettings,
            collapsed,
          },
        };
      });
    },
    [setInitialState],
  );

  return {
    ...layoutSettings,
    navTheme: navigationTheme,
    token: getNavigationToken(navigationTheme),
    layout: 'mix',
    splitMenus: false,
    selectedKeys: [
      history.location.pathname.startsWith('/notification')
        ? '/notification'
        : history.location.pathname,
    ],
    headerHeight: 44,
    fixedHeader: true,
    fixSiderbar: true,
    breadcrumbRender: false,
    actionsRender: () => {
      const { showHeader } = getCurrentLayoutVisibility();
      if (!showHeader) {
        return [];
      }

      return [
        <HeaderSessionContainer key="header-actions">
          <NotificationIcon />
          <ThemeToggle />
        </HeaderSessionContainer>,
      ];
    },
    avatarProps: {
      render: () => {
        const { showHeader } = getCurrentLayoutVisibility();
        if (!showHeader) {
          return null;
        }
        return (
          <HeaderSessionContainer>
            <HeaderUserMenu
              appId={BizAppConfig.appSsoClientId}
              personalCenterUrl={BizAppConfig.appPersonalCenterUrl}
              workbenchUrl={BizAppConfig.appWorkbenchUrl}
              tenantManagementUrl={BizAppConfig.appTenantManagementUrl}
              openPlatformUrl={BizAppConfig.appOpenPlatformUrl}
            />
          </HeaderSessionContainer>
        );
      },
    },
    waterMarkProps: {
      content: '',
    },
    footerRender: () => {
      const { showFooter } = getCurrentLayoutVisibility();
      if (!showFooter) {
        return false;
      }
      return null;
    },
    headerRender: (_props, dom) => {
      const { showHeader } = getCurrentLayoutVisibility();
      return showHeader ? dom : false;
    },
    menuRender: (_props, dom) => {
      const { showNav } = getCurrentLayoutVisibility();
      return showNav ? dom : false;
    },
    headerTitleRender: (logo) => (
      <HeaderTitle logo={logo} appTitle={appTitle} />
    ),
    onPageChange: () => {
      const { pathname } = history.location;
      if (publicPaths.includes(pathname)) {
        return;
      }

      void ensureValidAccessToken();
    },
    collapsedButtonRender: (isCollapsed, defaultDom) =>
      React.isValidElement(defaultDom)
        ? React.cloneElement(
            defaultDom as React.ReactElement<React.HTMLAttributes<HTMLElement>>,
            {
              role: 'button',
              tabIndex: 0,
              'aria-label': isCollapsed ? '展开侧栏' : '折叠侧栏',
              'aria-expanded': !isCollapsed,
              onKeyDown: (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleSiderCollapse(!isCollapsed);
                }
              },
            },
          )
        : defaultDom,
    onCollapse: handleSiderCollapse,
    defaultCollapsed: true,
    collapsed,
    menuHeaderRender: undefined,
    siderWidth,
    childrenRender: (children) => {
      const { showNav } = getCurrentLayoutVisibility();
      return (
        <>
          <ThemeBoundary>{children}</ThemeBoundary>
          <SiderResizeHandle
            enabled={showNav && resizeEnabled}
            siderWidth={siderWidth}
            onResizeWidth={handleResizeWidth}
          />
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings: {
                    ...settings,
                    layout: 'mix',
                    splitMenus: false,
                    headerHeight: 44,
                  },
                }));
              }}
            />
          )}
        </>
      );
    },
    title: appTitle,
  };
};
